import React, { useState } from 'react';
import { StyleSheet } from 'react-native';
import { User } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNACard, DNADivider, DNAText, GridList } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAEmpty, EmptyVariant, GridListSingleColumnHeader, ListFieldConfig } from 'src/components/DNA';
import DNAUserDefaultRow from '../FlatList/Rows/default/DNAUserDefaultRow';
import sortBy from 'lodash/sortBy'
import { UserORM } from 'src/types/orms';

const styles = StyleSheet.create({
  header: {
    paddingVertical: 12,
  },
  wrapper: {
    minWidth: 860,
  },
});

interface Props {
  usersORM: UserORM[];
}

enum SortOrders {
  'ascending',
  'descending'
}
type SortOrder = keyof typeof SortOrders

type userFields = keyof User
interface SelectedSort {
  fieldName: userFields
  sortOrder: SortOrder
}

interface UserListFieldConfig extends ListFieldConfig {
  unsortable?: boolean
}

const fields: UserListFieldConfig[] = [
  {
    fieldName: 'givenName',
    fieldLabel: 'Name',
    fieldType: 'LABEL',
    width: 2,
  },
  {
    fieldName: 'role',
    fieldLabel: 'Role',
    fieldType: 'LABEL',
    width: 1,
  },
  {
    fieldName: 'email',
    fieldLabel: 'Email/Phone',
    fieldType: 'LABEL',
    width: 3,
  },
  {
    fieldName: 'filters',
    fieldLabel: 'Filters',
    fieldType: 'LABEL',
    width: 3,
    unsortable: true,
  },
  {
    fieldName: 'status',
    fieldLabel: 'Status',
    fieldType: 'LABEL',
    width: 2,
  },
]

const DNAUsersList = (props : Props) => {
  const users = props.usersORM
  const [selectedSort, setSelectedSort] = useState<SelectedSort>(
    {
      fieldName:'givenName',
      sortOrder:'ascending',
    },
  )

  const sortedUsers = selectedSort.sortOrder === 'ascending'
    ? sortBy(users, (user) => user.model[selectedSort.fieldName])
    : sortBy(users, (user) => user.model[selectedSort.fieldName]).reverse()

  if (!users.length) {
    return (
      <DNACard appearance="float" style={{ overflow: 'hidden', borderRadius: 8 }}>
        <DNAEmpty emptyVariant={EmptyVariant.UsersListEmpty} />
      </DNACard>
    );
  }

  const toggleSort = (fieldName: userFields) => {
    const fieldIsActive = selectedSort.fieldName === fieldName
    if (fieldIsActive) {
      setSelectedSort(p => ({ ...p, sortOrder: selectedSort.sortOrder === 'ascending' ? 'descending' : 'ascending' }))
    } else {
      setSelectedSort({
        fieldName: fieldName,
        sortOrder: 'ascending',
      })
    }
  }

  const headerElements = fields.map(field => {
    const sortDir = selectedSort.fieldName === field.fieldName
      ? selectedSort.sortOrder === 'ascending'
        ? 'asc'
        : 'desc'
      : 'none';
    return (
      <GridList.Col span={field.width} key={field.fieldName}>
        {field.unsortable
          ? <DNAText status="tertiary" label>{field.fieldLabel}</DNAText>
          : <GridListSingleColumnHeader
              key={field.fieldLabel}
              field={field}
              style={styles.header}
              toggleSort={toggleSort}
              sortDir={sortDir}
          />
      }
      </GridList.Col>)
  })

  return (
    <DNABox>
      <DNACard style={styles.wrapper}>
        <GridList cols={'50px repeat(13, minmax(50px, 1fr))'} gap="12px">
          <GridList.Header style={{ backgroundColor: colors['color-gray-10'] }}>
            <GridList.Col style={[styles.header, { marginRight: 16 }]} span={1}>
              {/* ACTION ELLIPSIS BUTTON */}
            </GridList.Col>
            {headerElements}
          </GridList.Header>
          {/* CONTENT */}
          {
            sortedUsers.map((user) => (
              <React.Fragment key={user.model.id}>
                <DNADivider />
                <DNAUserDefaultRow user={user} />
              </React.Fragment>
            ))
          }
        </GridList>
      </DNACard>
    </DNABox>
  );
};

DNAUsersList.displayName = 'UsersList';

export default DNAUsersList;
